const SLIDE_W = 13.333;
const SLIDE_H = 7.5;

export const TM = {
  width: SLIDE_W,
  height: SLIDE_H,
  marginX: 1.11,
  padTop: 0.55,
  footerY: 6.9,
  footerH: 0.32,
  contentBottom: 6.72,
  accentH: 0.055,
  radius: 0.12,
  gapSm: 0.08,
  gapMd: 0.14,
  gapLg: 0.2,
};

export function contentWidth() {
  return TM.width - TM.marginX * 2;
}

export function estimateLines(text, widthIn, fontSize, charFactor = 0.52) {
  const value = String(text ?? "").trim();
  if (!value) {
    return 1;
  }
  const inchPerChar = (fontSize * charFactor) / 72;
  const perLine = Math.max(8, Math.floor(widthIn / inchPerChar));
  let lines = 1;
  let count = 0;
  for (const word of value.split(/\s+/)) {
    const add = count === 0 ? word.length : word.length + 1;
    if (count + add > perLine) {
      lines += 1;
      count = word.length;
    } else {
      count += add;
    }
  }
  return Math.max(1, lines);
}

export function textBoxHeight(text, widthIn, fontSize, {
  lineHeight = 1.2,
  maxLines = 4,
  bold = false,
  min = 0.22,
} = {}) {
  const lines = Math.min(
    maxLines,
    estimateLines(text, widthIn, fontSize, bold ? 0.56 : 0.5),
  );
  return Math.max(min, (lines * fontSize * lineHeight) / 72);
}

export function coverTitleFont(title) {
  const length = String(title ?? "").length;
  if (length > 80) {
    return 26;
  }
  if (length > 55) {
    return 28;
  }
  if (length > 36) {
    return 30;
  }
  return 34;
}

export function sectionTitleFont(title) {
  const length = String(title ?? "").length;
  if (length > 48) {
    return 20;
  }
  if (length > 32) {
    return 24;
  }
  return 26;
}

export function layoutCoverHeader({ title, subtitle, kicker }) {
  const width = contentWidth() - 1.6;
  let y = 1.05;
  const kickerH = kicker && String(kicker).length > 48 ? 0.36 : 0.24;
  const kickerBox = kicker ? { y, h: kickerH } : null;
  y += (kickerBox?.h ?? 0) + TM.gapMd;

  const titleFont = coverTitleFont(title);
  const titleH = textBoxHeight(title, width, titleFont, {
    lineHeight: 1.12,
    maxLines: 3,
    bold: true,
    min: 0.42,
  });
  const titleBox = { y, h: titleH, fontSize: titleFont, w: width };
  y += titleH + TM.gapMd;

  const dividerY = y;
  y += TM.gapMd;

  const subtitleH = subtitle
    ? textBoxHeight(subtitle, width, 13, { lineHeight: 1.4, maxLines: 2, min: 0.32 })
    : 0;
  const subtitleBox = subtitle ? { y, h: subtitleH, fontSize: 13, w: width } : null;

  return { kickerBox, titleBox, dividerY, subtitleBox };
}

export function layoutSectionHeader({ title, subtitle, kicker }) {
  const width = contentWidth();
  let y = TM.padTop;
  const kickerH = kicker && String(kicker).length > 52 ? 0.34 : 0.22;
  const kickerBox = kicker ? { y, h: kickerH } : null;
  y += (kickerBox?.h ?? 0) + TM.gapSm;

  const titleFont = sectionTitleFont(title);
  const titleH = textBoxHeight(title, width, titleFont, {
    lineHeight: 1.15,
    maxLines: 2,
    bold: true,
    min: 0.38,
  });
  const titleBox = { y, h: titleH, fontSize: titleFont };
  y += titleH + 0.06;

  const subtitleH = subtitle
    ? textBoxHeight(subtitle, width, 12, { lineHeight: 1.35, maxLines: 2, min: 0.26 })
    : 0;
  const subtitleBox = subtitle ? { y, h: subtitleH, fontSize: 12 } : null;
  y += subtitleH ? subtitleH + TM.gapSm : 0;

  const dividerY = y;
  y += TM.gapMd;

  return {
    kickerBox,
    titleBox,
    subtitleBox,
    dividerY,
    contentY: y,
    contentH: Math.max(2.4, TM.contentBottom - y),
  };
}
